/**
 * CasingSchematic — the concentric casing schematic for W3. Hand-SVG (no Plotly),
 * scaled by installation depth on Y and by casing OD on X, so the strings read as
 * nested tubes: the widest / shallowest (Conductor) on the outside, tapering in to
 * the narrowest / deepest (Production) at TD. Each string draws two casing walls
 * (a left + right vertical) down to its shoe, with a shoe tick at the base — the
 * classic concentric casing diagram, not the telescoping hole schematic of W2.
 *
 * Clicking a string (or its legend row) selects it, mirroring the grid ↔ schematic
 * highlight from the wireframe. Strings flagged by validateNesting render dashed +
 * in the warn hue. Rendered inside the shared ChartFrame so it owns the responsive
 * viewBox + depth gridlines from one place.
 */
import { ChartFrame } from "@components/chart/ChartFrame";
import type { CasingRow } from "@api/types";
import { typeVar, type NestingCode } from "./casingModel";

/** A string resolved to drawable geometry (depth + OD + colour + warn state). */
export interface SchematicString {
  key: string;
  label: string;
  od: number | null;
  topM: number;
  baseM: number;
  colorVar: string;
  warn: boolean;
}

interface CasingSchematicProps {
  rows: CasingRow[];
  rowKey: (row: CasingRow, index: number) => string;
  /** Row index → its nesting violations (parallel to rows). */
  nestingByRow: NestingCode[][];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  caption: string;
  ariaLabel: string;
}

const WIDTH = 300;
const HEIGHT = 460;
const PAD = { top: 22, right: 44, bottom: 16, left: 22 };

/** Half-width (px) of a string's tube for a given OD, at the widest scale. */
function halfWidthFor(od: number | null, maxOd: number): number {
  const d = od && od > 0 ? od : 1;
  const t = maxOd > 0 ? d / maxOd : 1;
  // Widest OD → ~92px half-width, narrowest → ~16px, so tubes visibly nest.
  return 16 + t * 76;
}

export function CasingSchematic({
  rows,
  rowKey,
  nestingByRow,
  selectedKey,
  onSelect,
  caption,
  ariaLabel,
}: CasingSchematicProps) {
  const strings: SchematicString[] = rows.map((r, i) => ({
    key: rowKey(r, i),
    label: r.string_name || `#${i + 1}`,
    od: r.casing_size,
    topM: r.top_installation ?? 0,
    baseM: r.base_installation_depth ?? r.top_installation ?? 0,
    colorVar: typeVar(r.type),
    warn: (nestingByRow[i]?.length ?? 0) > 0,
  }));

  const maxMd = Math.max(1, ...strings.map((s) => s.baseM), ...strings.map((s) => s.topM));
  const maxOd = Math.max(1, ...strings.map((s) => s.od ?? 0));

  // Depth gridlines every ~1000 m (or 2000 for deep wells), labels kept legible.
  const gridStep = maxMd > 6000 ? 2000 : 1000;
  const gridMarks: number[] = [];
  for (let d = gridStep; d < maxMd; d += gridStep) gridMarks.push(d);

  return (
    <div className="casing-schem">
      <div className="casing-schem-cap">
        <span>{caption}</span>
        <span className="tab-nums">0–{Math.round(maxMd).toLocaleString()} m</span>
      </div>
      <ChartFrame
        width={WIDTH}
        height={HEIGHT}
        padding={PAD}
        xDomain={[-1, 1]}
        yDomain={[0, maxMd]}
        yDown
        ariaLabel={ariaLabel}
      >
        {({ plot, sx, sy }) => {
          const cx = sx(0); // centreline x

          return (
            <g>
              {/* depth gridlines + labels */}
              {gridMarks.map((d) => (
                <g key={d}>
                  <line
                    x1={plot.x}
                    y1={sy(d)}
                    x2={plot.x + plot.w}
                    y2={sy(d)}
                    stroke="var(--line)"
                    strokeWidth={0.5}
                    strokeDasharray="2 3"
                    opacity={0.55}
                  />
                  <text
                    x={plot.x + plot.w + 4}
                    y={sy(d) + 3}
                    fontSize={9}
                    fill="var(--dim)"
                    className="tab-nums"
                  >
                    {d.toLocaleString()}
                  </text>
                </g>
              ))}

              {/* ground / wellhead line */}
              <line
                x1={plot.x}
                y1={sy(0)}
                x2={plot.x + plot.w}
                y2={sy(0)}
                stroke="var(--line)"
                strokeWidth={1.5}
              />
              <text x={plot.x + plot.w + 4} y={sy(0) + 3} fontSize={9} fill="var(--dim)">
                0 m
              </text>
              {/* wellhead cap */}
              <rect
                x={cx - 18}
                y={sy(0) - 10}
                width={36}
                height={10}
                rx={2}
                fill="var(--surface-3)"
                stroke="var(--line)"
              />

              {/* centreline */}
              <line
                x1={cx}
                y1={sy(0)}
                x2={cx}
                y2={sy(maxMd)}
                stroke="var(--dim)"
                strokeWidth={1}
                strokeDasharray="2 3"
                opacity={0.5}
              />

              {/* strings, widest drawn first so narrower inner strings sit on top */}
              {strings
                .map((s, i) => ({ s, i }))
                .sort((a, b) => halfWidthFor(b.s.od, maxOd) - halfWidthFor(a.s.od, maxOd))
                .map(({ s }) => {
                  const isSel = s.key === selectedKey;
                  const hw = halfWidthFor(s.od, maxOd);
                  const yTop = sy(s.topM);
                  const yBase = sy(s.baseM);
                  const stroke = s.warn ? "var(--warn)" : `var(${s.colorVar})`;
                  const strokeW = isSel ? 3 : 2;
                  return (
                    <g
                      key={s.key}
                      className={`casing-schem-str${s.warn ? " warn" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-label={s.label}
                      aria-pressed={isSel}
                      onClick={() => onSelect(s.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelect(s.key);
                        }
                      }}
                    >
                      {/* subtle fill band to make the selected string pop */}
                      {isSel && (
                        <rect
                          x={cx - hw}
                          y={yTop}
                          width={hw * 2}
                          height={Math.max(0, yBase - yTop)}
                          fill={stroke}
                          fillOpacity={0.1}
                        />
                      )}
                      {/* left + right casing walls */}
                      <line
                        x1={cx - hw}
                        y1={yTop}
                        x2={cx - hw}
                        y2={yBase}
                        stroke={stroke}
                        strokeWidth={strokeW}
                        strokeDasharray={s.warn ? "5 3" : undefined}
                      />
                      <line
                        x1={cx + hw}
                        y1={yTop}
                        x2={cx + hw}
                        y2={yBase}
                        stroke={stroke}
                        strokeWidth={strokeW}
                        strokeDasharray={s.warn ? "5 3" : undefined}
                      />
                      {/* casing shoe at the base (splayed ticks) */}
                      <path
                        d={`M ${cx - hw} ${yBase} L ${cx - hw - 6} ${yBase + 9} M ${cx + hw} ${yBase} L ${cx + hw + 6} ${yBase + 9}`}
                        stroke={stroke}
                        strokeWidth={isSel ? 2.4 : 1.8}
                        fill="none"
                        opacity={0.85}
                      />
                      {/* base depth callout on the right wall */}
                      <text
                        x={cx + hw + 8}
                        y={yBase + 3}
                        fontSize={8.5}
                        fill={s.warn ? "var(--warn)" : "var(--dim)"}
                        className="tab-nums casing-str-lbl"
                      >
                        {s.od != null ? `${s.od}″` : "—"}
                      </text>
                    </g>
                  );
                })}

              {/* TD marker at the deepest base */}
              <text
                x={cx}
                y={sy(maxMd) - 6}
                fontSize={9}
                fill="var(--dim)"
                textAnchor="middle"
              >
                TD {Math.round(maxMd).toLocaleString()} m
              </text>
            </g>
          );
        }}
      </ChartFrame>
    </div>
  );
}
